/* global location */
import algoliasearch from 'algoliasearch/lite'
import instantsearch from 'instantsearch.js'
import { buildSearchClient, typesenseEnabled, primaryIndexName, typesenseSponsoredIndex, withSponsoredFilter, buildSponsoredSearchClient, adapterOverrides, withSimilarQuery } from '../../libs/search/searchClient'
import { sortBy, analytics, stats, searchBox, refinementList, configure } from 'instantsearch.js/es/widgets'

import { getAlgoliaIndexFromUrl, getAlgoliaSponsoredIndex, setFiltersFromUrl, getIndicesForSortBySelector, setCollectionsQueryFromUrl, setRoomTypeQueryFromUrl, shouldRestrictCollectionTitle, addCollapseOnClick, isClickAnalyticsEnabled, isBot, getOptionalFilters, getSeekrSortBy } from '../../utils/search/helpers'
import { refineNumberFormat } from '../../utils/search/refineNumberFormat'
import { getCookie } from '../../utils/cookieHelpers'
import searchRouting from '../../utils/search/searchRouting'

import { nonSponsoredListingsPath, sponsoredListingsQuery, clearSponsoredListingsFromPage, setSponsoredListingsOnPage } from '../sponsored_listings/helpers'

import { setUpRefineSearchEvents, setUpSearchProductsEvent } from '../../analytics/DataLayerEvents'

import { desktopFilterWidgets, desktopPriceFilterWidgets, categoryHierarchyFilter } from './filterWidgets'
import { categoryTreeFilter } from './categoryTreeFilter'
import { hierarchyToPath, landingFallbackPath, multiSelectCategoriesEnabled, pageTrailPath } from '../../utils/search/categoryPaths'
import { getLocaleFromPath, prependLocale } from '../../utils/localeHelpers'
import { syncSelectAllRows, addSelectAllClickListener } from './selectAllFilters'
import browseTilesWidget from './browseTiles'
import { buildCurrentRefinements, buildPageCurrentRefinements } from '../../libs/search/filters/desktop/selectedRefinements'
import { activeFilterCountWidget } from './activeFilterCount'
import { onRenderCustomisations } from '../../libs/search/onRenderCustomisations'
import { ruleContexts } from '../../libs/search/ruleContexts'
import { customPaginationWidget } from '../../libs/search/customPaginationWidget'
import { t } from '../../utils/search/translations'

import { customHitsWidget } from './customHitsWidget'

import { DISJUNCTIVE_FACETS, CONJUNCTIVE_FACETS, HIERARCHICAL_FACETS } from './facetDeclarations'

// When the last category selection goes (untick, pill dismiss), fall back to
// the page's own scope — with no refinement, a reload re-seeds the URL
// path's category and shows different results. No-op off category routes.
function categoryScopeFallbackMiddleware ({ instantSearchInstance }) {
  return {
    onStateChange ({ uiState }) {
      const indexUiState = uiState[instantSearchInstance.indexName] || {}
      const selections = (indexUiState.refinementList || {}).category_paths || []
      if (selections.length > 0) return

      const fallback = landingFallbackPath(window.location)
      if (!fallback) return

      // On a product-type landing, re-seeding would phantom-tick the page
      // itself straight back; navigate up to the scope page instead.
      if (fallback !== pageTrailPath(window.location)) {
        window.location.assign(prependLocale('/' + hierarchyToPath(fallback), getLocaleFromPath(window.location.pathname)))
        return
      }

      instantSearchInstance.mainIndex.setIndexUiState(previous => ({
        ...previous,
        refinementList: { ...previous.refinementList, category_paths: [fallback] }
      }))
    }
  }
}

function getRoutingConfig () {
  return getShowcaseFilters().length > 0 ? true : searchRouting
}

function getShowcaseFilters () {
  const filtersElement = document.querySelector('[data-id="showcase-filters"]')
  return filtersElement ? filtersElement.innerText : ''
}

function getProxySearchParams () {
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop)
  })
  return params
}

function getSimilarQuery () {
  return getProxySearchParams().similar_query
}

function getCrossCategory () {
  return getProxySearchParams().cross_category
}

// The listing ids are passed in from the server, so we render them client side via Algolia query/render flow
function getClientSideListingIds () {
  const listingIdsElement = document.querySelector('[data-id="client-side-listing-ids"]')
  return listingIdsElement ? listingIdsElement.innerText : null
}

function parseHydrationBlob (raw) {
  try {
    return JSON.parse(raw)
  } catch (error) {
    if (window.Sentry) window.Sentry.captureException(error)
    return null
  }
}

// Builds InstantSearch `initialResults` from the server's hydration blob so facets / widgets /
// hierarchy render without a client query. null (→ client query runs) when the blob is absent
// or malformed, so this is always a safe enhancement.
function getInitialResults () {
  const element = document.querySelector('[data-id="algolia-initial-results"]')
  if (!element) return null

  const raw = element.textContent.trim()
  if (raw === '') return null

  const payload = parseHydrationBlob(raw)
  if (!payload) return null

  // Re-key the server's single-index payload under this instance's index and add the facet
  // declarations the widgets need to classify the server's facet data.
  const serverEntry = Object.values(payload)[0]
  if (!serverEntry || !Array.isArray(serverEntry.results) || !serverEntry.state) return null

  const indexName = primaryIndexName(getAlgoliaIndexFromUrl())

  return {
    [indexName]: {
      ...serverEntry,
      state: {
        ...serverEntry.state,
        index: indexName,
        disjunctiveFacets: DISJUNCTIVE_FACETS,
        facets: CONJUNCTIVE_FACETS,
        hierarchicalFacets: HIERARCHICAL_FACETS
      }
    }
  }
}

function getDisjunctiveFacets () {
  // Facets we want to make available for disjunctive (OR) queries
  return ['most_specific_category_name', 'sale', 'published_recently', 'commercially_accessible', 'luxury_items']
}

function getFacets () {
  return ['sale', 'published_recently', 'commercially_accessible', 'luxury_items']
}

function typesenseSponsoredSearch (client, collection, queryString, queryObject) {
  return client
    .search([{
      indexName: typesenseSponsoredIndex(collection),
      params: {
        ...queryObject,
        query: queryString,
        filters: withSponsoredFilter(queryObject.filters),
        hitsPerPage: 4
      }
    }])
    .then((response) => response.results[0])
}

function fetchSponsoredListings (searchFn, queryString, queryObject) {
  clearSponsoredListingsFromPage()
  searchFn(queryString, queryObject).then((results) => {
    if (results.hits?.length > 0) {
      // Ensure an even number of sponsored listings before rendering
      if (results.hits.length % 2 !== 0) {
        results.hits.pop()
      }

      // We already have vinterior_sponsored_listing in hit data
      // But this simplifies reusing rendering, so we don't have to
      // Add a new check inside it for where it's being used
      const sponsoredHits = results.hits.map(h => {
        return { ...h, renderSponsoredListing: true }
      })

      setSponsoredListingsOnPage(sponsoredHits)
    } else {
      setSponsoredListingsOnPage([])
    }
  }).catch((error) => {
    // waitSponsoredListings only resolves once the element is back.
    console.error('Sponsored listings query failed', error)
    setSponsoredListingsOnPage([])
  })
}

function getSearchParameters () {
  const isTracked = isClickAnalyticsEnabled()
  const onTypesense = typesenseEnabled()

  // Facet declarations and ruleContexts are engine-agnostic.
  const searchParameters = {
    ruleContexts: ruleContexts,
    disjunctiveFacets: getDisjunctiveFacets(),
    facets: getFacets()
  }

  if (!onTypesense) {
    // Algolia-only parameters; the adapter ignores them all.
    searchParameters.clickAnalytics = isTracked
    searchParameters.analytics = isTracked
    searchParameters.userToken = getCookie('_ALGOLIA')
    searchParameters.getRankingInfo = true
    searchParameters.attributesToRetrieve = ['*', '-description']
  }

  const similarquery = getSimilarQuery()
  if (similarquery && !onTypesense) searchParameters.similarQuery = similarquery

  // Already in the running engine's syntax: view renders it through SearchHelper#engine_filter_string
  const showcaseFilters = getShowcaseFilters()
  if (showcaseFilters !== '') searchParameters.filters = showcaseFilters

  const clientListingIds = getClientSideListingIds()
  if (clientListingIds) {
    const listingIdsArray = JSON.parse(clientListingIds)
    if (listingIdsArray.length === 0) {
      // Sourcing has no matches to show (e.g. grading failed): filter on an id
      // that cannot exist so the grid renders empty rather than falling back to
      // a default, unrelated search. The PLP header explains the empty state.
      searchParameters.filters = onTypesense ? 'sold:false && id:[-1]' : 'sold:false AND (objectID:-1)'
    } else if (onTypesense) {
      // `id:[...]` is the IN operator; objectID is not a Typesense field.
      searchParameters.filters = `sold:false && id:[${listingIdsArray.join(',')}]`
    } else {
      const objectIdsString = listingIdsArray.map((objectId) => `objectID:${objectId}`).join(' OR ')
      searchParameters.filters = `sold:false AND (${objectIdsString})`
    }
  } else if (getCrossCategory()) {
    // Hardcoded for now, but we could pass list of Categories we wanted to add in the query later
    searchParameters.filters = onTypesense
      ? 'category_hierarchy.lvl0:=[`Home Decor`,`Lighting`]'
      : 'category_hierarchy.lvl0:"Home Decor" OR category_hierarchy.lvl0:"Lighting"'
  }

  if (!similarquery) {
    const optionalFilters = getOptionalFilters()
    // Algolia only: on Typesense the boosts are a sort_by baked into the
    // adapter instance (adapterOverrides + getSeekrSortBy below); setting
    // them here would be silently dropped.
    if (optionalFilters && !onTypesense) searchParameters.optionalFilters = optionalFilters
  }

  return searchParameters
}

function skipSponsoredListingsRequest () {
  const hitsElement = document.getElementById('hits')
  if (!hitsElement) return
  const firstRender = JSON.parse(hitsElement.getAttribute('data-first-render'))
  // This is the code in case we want to skip based on page
  // required that we pass `queries`, see method call
  // const page = queries?.[0]?.params?.page || 0

  const response = firstRender === true || nonSponsoredListingsPath(location) || isBot()
  return response
}

export default function () {
  // Baked into the adapter instance because none of these can travel on a
  // request: the similar-query profile, the collection page's title-only
  // query_by, and the viewer's affinity sort_by (without which only the
  // hydrated first paint is personalised and the grid reshuffles on the first
  // shopper action).
  const similarquery = getSimilarQuery()
  const { client: originalClient, engine } = buildSearchClient(
    adapterOverrides({
      similarQuery: !!similarquery,
      restrictToTitle: shouldRestrictCollectionTitle(),
      seekrSortBy: getSeekrSortBy()
    })
  )
  const onTypesense = engine === 'typesense'

  // Memoised inside buildSponsoredSearchClient, on the same override key.
  const sponsoredClient = () => buildSponsoredSearchClient()

  // Patched client: one extra sponsored-listings query per PLP search. On
  // Typesense it hits the same collection with a filter and an
  // impressions-first sort (typesenseSponsoredIndex).
  const searchClient = {
    ...originalClient,
    searchForFacetValues (queries) {
      return originalClient.searchForFacetValues(withSimilarQuery(queries, similarquery, onTypesense))
    },
    search (queries) {
      const requests = withSimilarQuery(queries, similarquery, onTypesense)

      if (skipSponsoredListingsRequest()) {
        return originalClient.search(requests)
      }

      const queryObject = sponsoredListingsQuery(search.helper)
      const queryString = search.helper.state?.query ? search.helper.state.query : ''

      if (onTypesense) {
        const collection = primaryIndexName(getAlgoliaIndexFromUrl())
        fetchSponsoredListings(
          (q, opts) => typesenseSponsoredSearch(sponsoredClient(), collection, q, opts),
          queryString,
          queryObject
        )
      } else {
        const client = algoliasearch(process.env.ALGOLIASEARCH_APPLICATION_ID, process.env.ALGOLIASEARCH_API_KEY_SEARCH, { protocol: 'https:' })
        const index = client.initIndex(getAlgoliaSponsoredIndex())
        fetchSponsoredListings((q, opts) => index.search(q, opts), queryString, queryObject)
      }

      return originalClient.search(requests)
    }
  }

  // When set, InstantSearch renders from server data and skips the initial client query
  const initialResults = getInitialResults()

  const search = instantsearch({
    indexName: primaryIndexName(getAlgoliaIndexFromUrl()),
    searchClient: searchClient,
    routing: getRoutingConfig(),
    searchFunction: function (helper) {
      const page = helper.getPage()
      setFiltersFromUrl(helper)
      setCollectionsQueryFromUrl(helper)
      setRoomTypeQueryFromUrl(helper)
      helper.setPage(page).search()
    },
    future: {
      // Starting from the next major version, InstantSearch will change how widgets state is preserved when they are removed.
      // InstantSearch will keep the state of unmounted widgets to be usable by other widgets with the same attribute.
      // We recommend setting `future.preserveSharedStateOnUnmount` to true to adopt this change today.
      // To stay with the current behaviour and remove this warning, set the option to false.
      // See documentation: https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-future
      preserveSharedStateOnUnmount: false
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-persisthierarchicalrootcount
      // persistHierarchicalRootCount: boolean, // TODO: play with this later, default false
    }
  })

  search.addWidgets([
    configure(getSearchParameters())
  ])

  // TODO: renderOptions should already the createURL function.
  // Remove this arrow function
  search.addWidget({ render: (renderOptions) => onRenderCustomisations(renderOptions, search) })

  search.addWidgets(desktopPriceFilterWidgets)

  // TODO: addWidget deprecated but supported in 4.x change to addWidgets([])
  // Worry about it later
  search.addWidget(customHitsWidget(
    { containerNode: '#hits' }
  ))

  search.addWidget(
    // deprecated in favor of insights middleware. supported in 4.x but won't be in 5.x
    // TODO: check if the "push" is more instant with the middleware instead of this
    // as this has always been VERY annoying
    analytics({
      pushFunction: function (formattedParameters, state, results) {
        if (!isBot()) {
          setUpSearchProductsEvent(window.location.pathname, results, state, formattedParameters)
          const { queryID } = results
          setUpRefineSearchEvents(formattedParameters, queryID)
        }
      },
      triggerOnUIInteraction: true,
      pushInitialSearch: true,
      pushPagination: true
    })
  )

  if (document.getElementById('vjs-view-products-button')) {
    console.log('vjs-view-products-button found')
    search.addWidget(
      stats({
        container: '#vjs-view-products-button',
        templates: {
          text: (data) => {
            return `${t('view_verb')} ${refineNumberFormat(data.nbHits)} ${t('products_word')}`
          }
        }
      })
    )
  } else {
    search.addWidget(
      stats({
        container: '#number-of-listings',
        templates: {
          text: (data) => {
            if (!data.hasManyResults) return ''
            return `${t('total_listings_prefix')} ${refineNumberFormat(data.nbHits)}`
          }
        }
      })
    )
  }

  if (document.getElementById('search-term-stats')) {
    search.addWidget(
      stats({
        container: '#search-term-stats',
        templates: {
          text: (data) => {
            if (!data.hasManyResults) return ''
            if (!data.query) return ''
            return `${refineNumberFormat(data.nbHits)} ${t('results_for')} ${data.query}`
          }
        }
      })
    )
  }

  if (document.getElementById('similar-query-stats')) {
    search.addWidget(
      stats({
        container: '#similar-query-stats',
        templates: {
          text: (data) => {
            if (!data.hasManyResults) return ''
            return `${refineNumberFormat(data.nbHits)} ${t('similar_products_to')} "${data.query}"`
          }
        }
      })
    )
  }

  // TODO: Part 2 - Destroy _var_1 replicas we don't use
  // Create _price_asc and _price_desc replicas
  // specific to be used for trade users
  // Index for sort by configured here
  const sortByFilter = sortBy({
    container: '#sort-by',
    autoHideContainer: true,
    items: getIndicesForSortBySelector(search.indexName)
  })

  // This adds a query string on to the end of the end of the url
  // whenever a user uses the search bar at the top of our PLPs.
  const searchWidget = searchBox({
    container: '#hidden-search-box'
  })

  const userFilter = refinementList({
    container: '#user-slug',
    attribute: 'user_slug'
  })

  const tagsFilter = refinementList({
    container: '#tags',
    attribute: '_tags'
  })

  search.addWidgets([
    searchWidget,
    userFilter,
    tagsFilter,
    customPaginationWidget({
      container: document.querySelector('#pagination')
    })
  ])

  // Redirect users according to Rules configured in Algolia
  search.addWidgets([
    {
      render ({ results }) {
        if (
          results &&
          results.renderingContent &&
          results.renderingContent.redirect
        ) {
          window.location.href = results.renderingContent.redirect.url
        }
      }
    }
  ])

  search.addWidgets(desktopFilterWidgets)
  if (multiSelectCategoriesEnabled()) {
    search.addWidget(categoryTreeFilter({ container: '#category-hierarchy-filter' }))
    search.use(categoryScopeFallbackMiddleware)
  } else {
    search.addWidget(categoryHierarchyFilter)
  }
  search.addWidget(buildCurrentRefinements())
  search.addWidget(buildPageCurrentRefinements())
  search.addWidget(sortByFilter)
  search.addWidget(activeFilterCountWidget())
  const browseTiles = browseTilesWidget()
  if (browseTiles) search.addWidget(browseTiles)
  // Must come after the panel widgets: it reads and patches their rendered DOM
  search.addWidget({ render: syncSelectAllRows })

  // InstantSearch reads _initialResults in start(), not as a constructor option.
  if (initialResults) search._initialResults = initialResults
  search.start()
  addCollapseOnClick()
  addSelectAllClickListener(search)
};
